import React, { useCallback, useEffect, useRef, useState } from "react";

import { SearchService } from "../../../../services/searchService";
import { User } from "../../../../redux/type";

interface SearchUserModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onUserSelect: (users: User[]) => void;
}

const SearchUserModal: React.FC<SearchUserModalProps> = ({
  isOpen,
  closeModal,
  onUserSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on new search
    setHasMorePages(true); // Reset infinite scroll state
    await searchUsers(1);
  };

  const searchUsers = useCallback(
    async (page: number, append: boolean = false, searchQuery?: string) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Use the provided searchQuery or fall back to current searchTerm
      const queryToUse = searchQuery ?? searchTerm;

      try {
        const response = await SearchService.searchUsersByName({
          name: queryToUse,
          page: page,
        });
        const newUsers = response.data.users || [];

        if (append) {
          setUsers((prev) => [...prev, ...newUsers]);
        } else {
          setUsers(newUsers);
        }

        setCurrentPage(page);

        // Set pagination info from response
        const total = response.data.pagination?.total || 0;
        const limit = response.data.pagination?.limit || 10;
        const totalPages = Math.ceil(total / limit);

        console.log("Pagination debug:", {
          total,
          limit,
          totalPages,
          currentPage: page,
          newUsersLength: newUsers.length,
          hasMore:
            newUsers.length > 0 &&
            newUsers.length === limit &&
            page < totalPages,
          searchQuery: queryToUse,
        });

        setTotalPages(totalPages);
        setTotalUsers(total);

        // Check if there are more pages
        const hasMore =
          newUsers.length > 0 && newUsers.length === limit && page < totalPages;
        setHasMorePages(hasMore);
      } catch (err) {
        setError("Failed to fetch users");
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [searchTerm]
  );

  const handleCheckboxChange = (user: User) => {
    setSelectedUsers((prev) => {
      const exists = prev.some((u) => u.user_id === user.user_id);
      if (exists) {
        return prev.filter((u) => u.user_id !== user.user_id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleRemoveSelectedUser = (user: User) => {
    setSelectedUsers((prev) => prev.filter((u) => u.user_id !== user.user_id));
  };

  const handleConfirm = () => {
    onUserSelect(selectedUsers);
    closeModal();
    setSelectedUsers([]);
  };

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || loadingMore || !hasMorePages) return;

    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

    if (isAtBottom) {
      searchUsers(currentPage + 1, true, searchTerm);
    }
  }, [currentPage, loadingMore, hasMorePages, searchUsers, searchTerm]);

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  if (!isOpen) return null;

  return (
    <div className="custom-modal-overlay" onClick={closeModal}>
      <div
        className="custom-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="custom-close-button" onClick={closeModal}>
          ×
        </button>
        <div className="briefings-modal">
          <h2 style={{ marginBottom: "20px", color: "#333" }}>Search Users</h2>
          <form onSubmit={handleSearch} style={{ marginBottom: "16px" }}>
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: "8px", width: "70%", marginRight: "8px" }}
            />
            <button
              className="select-users-button"
              type="submit"
              style={{ padding: "8px 16px" }}
              disabled={loading}
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </form>
          {error && (
            <div style={{ color: "red", marginBottom: "8px" }}>{error}</div>
          )}
          <div
            className="briefings-lists-container"
            style={{ display: "flex", gap: 24 }}
          >
            {/* Left: Search Results */}
            <div
              ref={scrollContainerRef}
              className="briefings-list"
              style={{
                maxHeight: 300,
                overflowY: "auto",
                flex: 1,
                borderRight: "1px solid #eee",
                paddingRight: 16,
              }}
            >
              <h3 style={{ color: "#333", marginBottom: 8 }}>Search Results</h3>
              {users.length > 0 ? (
                users.map((user, idx) => {
                  const checked = selectedUsers.some(
                    (u) => u.user_id === user.user_id
                  );
                  return (
                    <div
                      key={user.user_id}
                      className="briefing-item"
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                      onClick={() => handleCheckboxChange(user)}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleCheckboxChange(user)}
                        style={{ marginRight: 12 }}
                      />
                      <div className="briefing-content">
                        <h4 style={{ margin: "0 0 8px 0", color: "#555" }}>
                          {user.name || user.user_id}
                        </h4>
                        <small style={{ color: "#999", fontSize: "12px" }}>
                          ID: {user.user_id}
                        </small>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p style={{ color: "#666", textAlign: "center" }}>
                  {loading ? "Searching..." : "No users found"}
                </p>
              )}
              {/* Loading More Indicator */}
              {loadingMore && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  Loading more users...
                </div>
              )}
              {totalUsers > 0 && (
                <div
                  style={{
                    marginTop: "8px",
                    textAlign: "center",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  {users.length} of {totalUsers} users loaded
                </div>
              )}
            </div>
            {/* Right: Selected Users */}
            <div
              className="selected-users-list"
              style={{
                maxHeight: 300,
                overflowY: "auto",
                flex: 1,
                paddingLeft: 16,
              }}
            >
              <h3 style={{ color: "#333", marginBottom: 8 }}>Selected Users</h3>
              {selectedUsers.length > 0 ? (
                selectedUsers.map((user) => (
                  <div
                    key={user.user_id}
                    className="briefing-item"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <div className="briefing-content">
                      <h4 style={{ margin: "0 0 8px 0", color: "#555" }}>
                        {user.name || user.user_id}
                      </h4>
                      <small style={{ color: "#999", fontSize: "12px" }}>
                        ID: {user.user_id}
                      </small>
                    </div>
                    <button
                      onClick={() => handleRemoveSelectedUser(user)}
                      style={{
                        marginLeft: 12,
                        background: "#ff4d4f",
                        color: "white",
                        border: "none",
                        borderRadius: 4,
                        padding: "4px 8px",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))
              ) : (
                <p style={{ color: "#666", textAlign: "center" }}>
                  No users selected
                </p>
              )}
            </div>
          </div>
          <div
            className="modal-actions"
            style={{ marginTop: "20px", textAlign: "right" }}
          >
            <button
              className="select-users-button"
              onClick={handleConfirm}
              disabled={selectedUsers.length === 0}
              style={{
                marginRight: 10,
                opacity: selectedUsers.length === 0 ? 0.6 : 1,
              }}
            >
              Confirm
            </button>
            <button
              className="cancel-button"
              onClick={closeModal}
              style={{
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchUserModal;
